import { createContext, useContext, useState, type CSSProperties } from "react";
import { Disclosure, Transition } from "@headlessui/react";
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon";
import useResizeObserver from "use-resize-observer";

import cx from "@hsl/core/utils/cx";

import useTranslation, { Translate } from "../../../hooks/useTranslation";
import { ErrorBoundary } from "../../GenericErrorBoundary";

interface Props {
    accordions: {
        label: string;
        content: React.ReactNode | (() => React.ReactNode);
        select?: {
            label: string;
            date?: string;
            value: string;
        }[];
    }[];
    initiallyOpen?: string;
    onChange?(key: string): void;
}

export const FundCardAccordions = ({
    accordions,
    onChange,
    initiallyOpen,
}: Props) => {
    const [currentlyOpen, setCurrentlyOpen] = useState<string | undefined>(
        initiallyOpen ?? accordions[0]!.label,
    );
    const handleChange = (label: string) => {
        setCurrentlyOpen(label);
        onChange && onChange(label);
    };
    return (
        <div className="flex w-full flex-col">
            {accordions.map((accordion) => {
                return (
                    <Accordion
                        key={accordion.label}
                        label={accordion.label}
                        select={accordion.select}
                        currentlyOpen={currentlyOpen}
                        setCurrentlyOpen={handleChange}
                    >
                        {accordion.content}
                    </Accordion>
                );
            })}
        </div>
    );
};

interface AccordionProps {
    label: string;
    children: React.ReactNode | (() => React.ReactNode);
    select?: {
        label: string;
        date?: string;
        value: string;
    }[];
    currentlyOpen?: string;
    setCurrentlyOpen: (label: string) => void;
}

const AccordionContext = createContext<string | undefined>(undefined);

export const useAccordionContext = () => {
    return useContext(AccordionContext);
};

const Accordion = ({
    label,
    children,
    select,
    currentlyOpen,
    setCurrentlyOpen,
}: AccordionProps) => {
    const [selected, setSelected] = useState(select?.[0]?.value);
    const { ref, height } = useResizeObserver<HTMLDivElement>({});
    const T = useTranslation();

    const renderSelect = (select: Required<AccordionProps>["select"]) => {
        const selectedItem = select.find((item) => item.value === selected);
        const selectedLabel = selectedItem?.label ?? "";
        const selectedDate = selectedItem?.date;

        return (
            <div className="ml-2 border-l pl-2">
                <label
                    className="relative -my-2 flex flex-col px-1 py-2"
                    onClick={(evt) => {
                        evt.stopPropagation();
                    }}
                >
                    <p className="flex flex-row whitespace-nowrap text-xs font-medium">
                        <Translate>{selectedLabel}</Translate>
                        <ChevronDownIcon width={14} className="ml-6 shrink-0" />
                    </p>
                    {selectedDate && (
                        <p className="w-full whitespace-nowrap text-left text-[10px]">
                            {selectedDate}
                        </p>
                    )}
                    <select
                        className="absolute left-0 top-0 h-full w-full opacity-[0.01]"
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                    >
                        {select.map((option) => (
                            <option key={option.value} value={option.value}>
                                {T(option.label).result}
                                {option.date && ` (${option.date})`}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        );
    };

    return (
        <AccordionContext.Provider value={selected}>
            <Disclosure defaultOpen={currentlyOpen === label}>
                {({ open, close }) => {
                    if (open && currentlyOpen !== label) {
                        // pass a fake div so that the button doesn't get focussed
                        close({ current: document.createElement("div") });
                    }

                    return (
                        <>
                            <Disclosure.Button
                                className={cx(
                                    "text-charcoal relative z-10 transition-colors",
                                    open &&
                                        "bg-white-smoke border-dark-blue border-l-[5px]",
                                )}
                                onClick={() => setCurrentlyOpen(label)}
                            >
                                <div
                                    className={cx(
                                        "border-platinum flex w-full flex-row items-center justify-between border-b px-4 py-3",
                                        open && "pl-3",
                                    )}
                                >
                                    <div className="flex flex-1 flex-row items-center justify-between">
                                        <span className="text-sm font-medium">
                                            <Translate>{label}</Translate>
                                        </span>
                                        <ChevronDownIcon
                                            width={20}
                                            className={cx(
                                                "transition-transform",
                                                open && "rotate-180 transform",
                                            )}
                                        />
                                    </div>
                                    {open && select && renderSelect(select)}
                                </div>
                            </Disclosure.Button>
                            <Transition
                                className="transition-all duration-200 ease-out"
                                enterFrom="max-h-0 opacity-0"
                                enterTo={`max-h-[var(--panel-height)] opacity-100`}
                                leaveFrom={`max-h-[var(--panel-height)] opacity-100`}
                                leaveTo="max-h-0 opacity-0"
                                style={
                                    {
                                        "--panel-height": `${height}px`,
                                    } as CSSProperties
                                }
                            >
                                <Disclosure.Panel>
                                    <div ref={ref}>
                                        <div className="border-l-dark-blue border-b-platinum -pt-4 inline-block w-full border-b border-l-[5px] pb-4 pl-3">
                                            <ErrorBoundary>
                                                {typeof children === "function"
                                                    ? children()
                                                    : children}
                                            </ErrorBoundary>
                                        </div>
                                    </div>
                                </Disclosure.Panel>
                            </Transition>
                        </>
                    );
                }}
            </Disclosure>
        </AccordionContext.Provider>
    );
};
